import type { Call, Gig, Item, Position, Quote, QuoteLineItem } from './types.js';
import { quoteTotals } from './quote.js';
import { wallTimeIn, shiftDays, startOfDayIn, calendarDayNumber } from './timezone.js';

// Re-exported for existing importers (the helpers now live in timezone.ts).
export { shiftDays, startOfDayIn };

// Pure planning for "Duplicate Gig": given a source gig's calls, positions
// and active quote, produce the docs to write for the copy on a new date.
// No IO — the client layer owns reading the source and writing the batch.
//
// What carries over: the gig's descriptive fields, every call (shifted to
// the new date), every position (budgets/rates intact, callIds remapped,
// reset to OPEN with no bookings), and the active quote's non-labor lines
// (catalog items + manual lines). What does not: bookings/offers, contracts,
// invoice ids, labor quote lines (derived data — "Recompute from Budget"
// regenerates them from the copied positions), deposit amount, gig state.

export type DuplicatePriceMode = 'legacy' | 'current';

export interface ItemPriceDiff {
  lineId: string;
  description: string;
  legacyUnitCost: number;
  // undefined when the catalog item no longer exists — such a line always
  // keeps its legacy values, whichever price mode is chosen.
  currentUnitCost?: number;
  missingFromCatalog: boolean;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// An 'item' line is refreshable only if it still points at a catalog item.
function catalogItemFor(line: QuoteLineItem, catalog: Item[]): Item | undefined {
  if (line.source !== 'item' || !line.itemId) return undefined;
  return catalog.find(i => i.id === line.itemId);
}

// Lines a duplicate would carry whose price differs from today's catalog, or
// whose catalog item has since been deleted. Empty when nothing needs a
// decision from the user.
export function diffItemPrices(lines: QuoteLineItem[], catalog: Item[]): ItemPriceDiff[] {
  const diffs: ItemPriceDiff[] = [];
  for (const line of lines) {
    if (line.source !== 'item' || !line.itemId) continue;
    const item = catalogItemFor(line, catalog);
    if (!item) {
      diffs.push({
        lineId: line.id,
        description: line.description,
        legacyUnitCost: line.unitCost,
        missingFromCatalog: true,
      });
    } else if (round2(item.unitCost) !== round2(line.unitCost)) {
      diffs.push({
        lineId: line.id,
        description: line.description,
        legacyUnitCost: line.unitCost,
        currentUnitCost: item.unitCost,
        missingFromCatalog: false,
      });
    }
  }
  return diffs;
}

// --- planner ---------------------------------------------------------------

export interface PlanGigDuplicateInput {
  orgId: string;
  gig: Gig;
  calls: Call[];
  positions: Position[];
  // The source gig's active quote, if any. Only its non-labor lines matter.
  quote: Quote | null;
  catalog: Item[];
  priceMode: DuplicatePriceMode;
  // New gig date as YYYY-MM-DD, interpreted in `timeZone`.
  newDate: string;
  // Defaults to the source gig's own name.
  name?: string;
  // Fallback zone, and `timeZoneFor` (zone of a venue by id) when known.
  timeZone: string;
  timeZoneFor?: (venueId: string | undefined) => string | undefined;
  newGigId: string;
  // Id generator for the copied calls and quote lines.
  newId: () => string;
}

type CallData = Omit<Call, 'createdAt' | 'updatedAt'>;
type PositionData = Omit<Position, 'createdAt' | 'updatedAt'>;
type GigData = Omit<Gig, 'createdAt' | 'updatedAt'>;

export interface GigDuplicatePlan {
  gig: GigData;
  calls: CallData[];
  positions: PositionData[];
  // null when the source has no quote, or no non-labor lines to carry.
  quote: {
    clientId: string;
    currency: string;
    lineItems: QuoteLineItem[];
    taxRate?: number;
    notes?: string;
    subtotal: number;
    tax: number;
    total: number;
  } | null;
  dayOffset: number;
}

// Firestore rejects `undefined` field values; drop them so optional fields
// that were never set on the source stay absent on the copy.
function defined<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export function planGigDuplicate(input: PlanGigDuplicateInput): GigDuplicatePlan {
  const { orgId, gig, calls, positions, quote, catalog, priceMode, newDate, newGigId, newId } = input;
  // Zones come from the venues (by id); `input.timeZone` is the fallback for
  // a venue that's unset/unknown or has no zone.
  const zoneOf = (venueId?: string) => input.timeZoneFor?.(venueId) ?? input.timeZone;
  const timeZone = zoneOf(gig.venueId);

  const sourceDay = calendarDayNumber(wallTimeIn(gig.date, timeZone));
  const [ny, nm, nd] = newDate.split('-').map(Number);
  const dayOffset = calendarDayNumber({ y: ny, m: nm, d: nd }) - sourceDay;

  const callIdMap = new Map<string, string>();
  const newCalls: CallData[] = calls.map(c => {
    const id = newId();
    callIdMap.set(c.id, id);
    return defined({
      id,
      gigId: newGigId,
      orgId,
      type: c.type,
      // Shifted by calendar day in the call's own venue zone so a 7pm call
      // stays 7pm there across a DST change.
      startTime: shiftDays(c.startTime, dayOffset, c.venueId ? zoneOf(c.venueId) : timeZone),
      endTime: shiftDays(c.endTime, dayOffset, c.venueId ? zoneOf(c.venueId) : timeZone),
      venueId: c.venueId,
      notes: c.notes,
    });
  });

  const newPositions: PositionData[] = positions.map(p =>
    defined({
      id: newId(),
      gigId: newGigId,
      orgId,
      role: p.role,
      label: p.label,
      quantity: p.quantity,
      rate: p.rate,
      rateType: p.rateType,
      maxBudget: p.maxBudget,
      rateCardId: p.rateCardId,
      rateCardRole: p.rateCardRole,
      // A link to a call that no longer exists is dropped, not carried.
      callIds: p.callIds.map(id => callIdMap.get(id)).filter((id): id is string => !!id),
      venueId: p.venueId,
      scopeDate: p.scopeDate ? shiftDays(p.scopeDate, dayOffset, timeZone) : undefined,
      bookings: [],
      state: 'OPEN' as const,
    }),
  );

  const nonLabor = (quote?.lineItems ?? []).filter(l => l.source !== 'labor');
  let quotePlan: GigDuplicatePlan['quote'] = null;
  if (quote && nonLabor.length > 0) {
    const lineItems = nonLabor.map(line => {
      const { locked: _locked, ...rest } = line;
      const copy: QuoteLineItem = { ...rest, id: `${line.source}_${newId()}` };
      const item = priceMode === 'current' ? catalogItemFor(line, catalog) : undefined;
      if (item) {
        copy.description = item.name;
        copy.unitCost = item.unitCost;
        copy.lineTotal = round2(item.unitCost * line.quantity);
        copy.itemDescription = item.description;
        copy.productKey = item.productKey;
      }
      return defined(copy);
    });
    quotePlan = defined({
      clientId: gig.clientId,
      currency: quote.currency,
      lineItems,
      taxRate: quote.taxRate,
      notes: quote.notes,
      ...quoteTotals(lineItems, quote.taxRate),
    });
  }

  return {
    gig: defined({
      id: newGigId,
      orgId,
      clientId: gig.clientId,
      clientName: gig.clientName,
      name: input.name?.trim() || gig.name,
      description: gig.description,
      venueId: gig.venueId,
      state: 'DRAFT' as const,
      date: startOfDayIn(newDate, timeZone),
      calls: newCalls.map(c => c.id),
      laborMarginPctOverride: gig.laborMarginPctOverride,
      activeQuoteId: undefined,
    }),
    calls: newCalls,
    positions: newPositions,
    quote: quotePlan,
    dayOffset,
  };
}
